import classNames from 'classnames'
import React from 'react'
import { twMerge } from 'tailwind-merge'

import { DietDetails } from '../../../api/services/dietbase/types.ts'
import Button from '../../../components/common/Button/Button.tsx'
import Loader from '../../../components/common/Loader/Loader.tsx'

const DietDetailsPopup = ({
  dietDetails,
  onClose,
  isPopup = true,
  isSqueezed = false,
  loading
}: {
  dietDetails: DietDetails | null | undefined
  onClose?: () => void
  isPopup?: boolean
  isSqueezed?: boolean
  loading?: boolean
}) => {
  const displayNutrions = (
    label: string,
    value: string,
    unit: string,
    color: string
  ) => {
    return (
      <div
        className={`flex flex-col items-center text-white ${color} rounded-2xl px-4 py-1`}
      >
        <p className={'text-[12px] h-[15px]'}>{label}</p>
        <span className={'font-bold'}>
          {`${Number(value).toFixed(0)} ${unit}`}
        </span>
      </div>
    )
  }

  return (
    <div
      className={
        'flex flex-col justify-center items-center gap-2 px-4 pt-4 pb-24'
      }
    >
      {loading && (
        <div
          className={'flex flex-col items-center justify-center px-40 py-20'}
        >
          <Loader size="large" />
          <p className={'w-full text-center'}>Proszę czekać...</p>
        </div>
      )}
      {!isSqueezed && !loading && (
        <p className={'w-full text-left'}>Podgląd diety bazowej</p>
      )}

      {dietDetails && !loading && (
        <p className={'font-bold text-[20px] my-8'}>{dietDetails.name}</p>
      )}

      {dietDetails &&
        dietDetails.day_events.map((event, index) => (
          <div key={`${event.start_at}-${index}`} className={'w-full'}>
            <div
              className={twMerge(
                classNames('flex bg-blue-400 p-4 text-white', {
                  'py-1': isSqueezed
                })
              )}
            >
              <p className={'font-bold'}>{event.start_at.slice(0, -3)}</p>
            </div>

            {event.meals.map((meal, index) => (
              <div
                key={`meal-${index}`}
                className={twMerge(
                  classNames('flex flex-col my-2 p-4 text-[14px] ', {
                    'my-0 py-0': isSqueezed
                  })
                )}
              >
                <p
                  className={twMerge(
                    classNames(
                      'font-bold text-[16px] pb-2 text-black dark:text-white',
                      { 'font-normal pb-0 text-[14px]': isSqueezed }
                    )
                  )}
                >
                  {index + 1}. {meal.name}
                </p>

                {/* nutritions */}
                {!isSqueezed && (
                  <div className={'flex flex-row gap-2 mb-4'}>
                    {displayNutrions(
                      'Energia',
                      meal.nutritional_info.energy,
                      ' kcal',
                      'bg-blue-400'
                    )}
                    {displayNutrions(
                      'Węglowodany',
                      meal.nutritional_info.carbohydrate,
                      ' g',
                      'bg-red-500'
                    )}
                    {displayNutrions(
                      'Białko',
                      meal.nutritional_info.protein,
                      ' g',
                      'bg-green-500'
                    )}
                    {displayNutrions(
                      'Tłuszcze',
                      meal.nutritional_info.fat,
                      ' g',
                      'bg-yellow-500'
                    )}
                  </div>
                )}

                {/* dishes*/}
                {!isSqueezed &&
                  meal.dishes.map((dish, index) => (
                    <div
                      className={
                        'mb-4 border-l-blue-300 border-l-2 border-b-gray-100 border-b-2 dark:border-b-gray-700 dark:border-b-1'
                      }
                    >
                      <p className={'px-1 font-bold'}>{dish.name}</p>
                      <div
                        key={`dish-${index}`}
                        className={'flex flex-col justify-between'}
                      >
                        {dish.portions.map((dishPortion) => (
                          <div
                            className={
                              'flex flex-row justify-between p-1 odd:bg-blue-100 dark:odd:bg-blue-950 even:bg-blue-50 dark:even:bg-blue-900 '
                            }
                          >
                            <p>{dishPortion.product_name}</p>
                            <div className={'flex flex-row gap-2'}>
                              <p>{dishPortion.amount_label}</p>
                              <p>
                                {Number(dishPortion.amount).toFixed(
                                  Number(dishPortion.amount) * 10 < 10 ? 1 : 0
                                )}{' '}
                                g
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {dish.recipe && (
                        <div className={'flex flex-col justify-between p-2'}>
                          <p className={'font-bold'}>Przepis</p>
                          <p className={''}>{dish.recipe}</p>
                        </div>
                      )}
                    </div>
                  ))}

                {/* portions */}
                {!isSqueezed &&
                  meal.portions.map((portion, index) => (
                    <div
                      key={`${meal.name}-portion-${index}`}
                      className={
                        'flex flex-row justify-between p-1 odd:bg-blue-100 dark:odd:bg-blue-950 even:bg-blue-50 dark:even:bg-blue-900 border-l-blue-300 border-l-2'
                      }
                    >
                      <p>{portion.product_name}</p>
                      <div className={'flex flex-row gap-2'}>
                        <p>{portion.amount_label}</p>
                        <p>
                          {Number(portion.amount).toFixed(
                            Number(portion.amount) * 10 < 10 ? 1 : 0
                          )}{' '}
                          g
                        </p>
                      </div>
                    </div>
                  ))}

                {!isSqueezed && meal.dishes.length === 0 && (
                  <div
                    className={
                      'flex flex-col justify-between p-2 border-l-blue-300 border-l-2 border-b-gray-100 border-b-2 dark:border-b-gray-700 dark:border-b-1 '
                    }
                  >
                    <p className={'font-bold'}>Przepis</p>
                    <p className={''}>{meal.recipe}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

      {isPopup && (
        <Button
          variant="filled"
          size="medium"
          color={'primary'}
          type="button"
          onClick={onClose}
          className={
            'fixed bottom-4 left-1/2 -translate-x-1/2 z-50 shadow-2xl opacity-70 hover:opacity-100'
          }
        >
          Zamknij
        </Button>
      )}
    </div>
  )
}

export default DietDetailsPopup
